import { decorators, ComponentChanged, OBSERVER_TYPE } from '@combos-fun/engine';

import { RendererManager, ContainerManager, RendererSystem, Renderer, Transform2D } from '@combos-fun/plugin-renderer';
import { Graphics, Text as TextEngine } from '@combos-fun/renderer-adapter';

import TextComponent from './component';
import { requestHostI18n } from './hostSdk';
import {
  addSourceTexts,
  hasMissingSourceTranslations,
  localizeText,
  mergeTranslations,
  shouldHoldForMissingTranslation,
  shouldRequestI18nForSourceTexts,
  sourceTextsSignature,
  untranslatedSourceTexts,
  type TextTranslations,
} from './localization';
import { parseTextColor, splitTextStyle } from './textStyle';

export interface TextSystemParams {
  /** BCP 47 locale of the source text, for example `zh-CN`. Omit to disable host-driven i18n. */
  sourceLocale?: string;
  /**
   * All source templates to translate on first `requestI18n`, including copy
   * for screens that are not mounted yet. The system also keeps a growing
   * session catalog of every later template it sees.
   */
  sourceTexts?: string[];
}

@decorators.componentObserver({
  Text: ['text', { prop: ['replacements'], deep: true }, { prop: ['style'], deep: true }],
})
export default class Text extends Renderer<TextSystemParams> {
  static systemName = 'Text';
  name: string = 'Text';
  texts: {
    [propName: number]: {
      text: TextEngine;
      background?: Graphics;
      component: TextComponent;
    };
  } = {};
  renderSystem: RendererSystem;
  rendererManager: RendererManager;
  containerManager: ContainerManager;
  private sourceLocale = '';
  private targetLocale = '';
  private translations: TextTranslations = {};
  /** Growing catalog of every source template seen this session. */
  private knownSourceTexts = new Set<string>();
  private lastRequestedSourceTexts = '';
  private i18nRequestScheduled = false;
  private i18nRequestId = 0;
  private firstI18nSettled = false;
  private i18nNeeded = false;
  private resolvedSourceTexts = new Set<string>();

  init(params?: TextSystemParams) {
    this.renderSystem = this.game.getSystem(RendererSystem) as RendererSystem;
    this.renderSystem.rendererManager.register(this);
    this.sourceLocale = params?.sourceLocale?.trim() ?? '';
    this.targetLocale = this.sourceLocale;
    this.declareSourceTexts(params?.sourceTexts);
  }
  /** Register extra source templates before or after ready. First request includes these. */
  declareSourceTexts(texts?: string[]) {
    this.rememberSourceTexts(texts);
    this.scheduleI18nRequest();
  }
  async beforeReady() {
    if (this.firstI18nSettled) return;
    this.update();
    if (!this.sourceLocale) {
      this.firstI18nSettled = true;
      return;
    }
    await this.requestI18nAndApply();
    if (
      this.i18nNeeded &&
      hasMissingSourceTranslations(this.collectSourceTexts(), this.translations)
    ) {
      await this.requestI18nAndApply();
    }
    this.firstI18nSettled = true;
    // needed:false refresh still ran with firstI18nSettled=false (empty hold).
    this.refreshAllText();
  }
  setI18n(locale: string, translations: TextTranslations) {
    const targetLocale = locale?.trim();
    if (!targetLocale || !translations || typeof translations !== 'object') return;
    this.targetLocale = targetLocale;
    this.translations = mergeTranslations(this.translations, translations);
    this.refreshAllText();
  }
  componentChanged(changed: ComponentChanged) {
    if (changed.componentName !== 'Text') return;
    if (changed.type === OBSERVER_TYPE.ADD) {
      const component = changed.component as TextComponent;
      const { pixiStyle } = splitTextStyle(component.style);
      const text = new TextEngine(this.resolveText(component), pixiStyle);
      const container = this.containerManager.getContainer(changed.gameObject.id);
      container.addChildAt(text, 0);
      this.texts[changed.gameObject.id] = {
        text,
        component,
      };
      this.rememberSourceTexts([component.text]);
      this.setSize(changed);
      this.syncBackground(changed.gameObject.id);
      this.scheduleI18nRequest();
    } else if (changed.type === OBSERVER_TYPE.REMOVE) {
      const entry = this.texts[changed.gameObject.id];
      const container = this.containerManager.getContainer(changed.gameObject.id);
      if (entry.background) {
        container.removeChild(entry.background);
        entry.background.destroy({ children: true });
      }
      container.removeChild(entry.text);
      entry.text.destroy({ children: true });
      delete this.texts[changed.gameObject.id];
    } else {
      this.change(changed);
      this.setSize(changed);
      this.syncBackground(changed.gameObject.id);
    }
  }
  change(changed: ComponentChanged) {
    const { text, component } = this.texts[changed.gameObject.id];
    if (changed.prop.prop[0] === 'text' || changed.prop.prop[0] === 'replacements') {
      if (changed.prop.prop[0] === 'text') this.rememberSourceTexts([component.text]);
      text.text = this.resolveText(component);
      if (changed.prop.prop[0] === 'text') this.scheduleI18nRequest();
    } else if (changed.prop.prop[0] === 'style') {
      const { pixiStyle } = splitTextStyle((changed.component as TextComponent).style);
      Object.assign(text.style, pixiStyle);
    }
  }
  setSize(changed: ComponentChanged) {
    const transform = changed.gameObject.getComponent(Transform2D);
    if (!transform) return;
    transform.size.width = this.texts[changed.gameObject.id].text.width;
    transform.size.height = this.texts[changed.gameObject.id].text.height;
  }
  private syncBackground(id: number) {
    const entry = this.texts[id];
    if (!entry) return;
    const color = parseTextColor(splitTextStyle(entry.component.style).backgroundColor);
    const container = this.containerManager.getContainer(id);
    if (color == null) {
      if (entry.background) {
        container.removeChild(entry.background);
        entry.background.destroy({ children: true });
        entry.background = undefined;
      }
      return;
    }
    if (!entry.background) {
      entry.background = new Graphics();
      container.addChildAt(entry.background, 0);
    }
    const width = entry.text.width;
    const height = entry.text.height;
    entry.background.clear();
    if (width > 0 && height > 0) {
      entry.background.rect(0, 0, width, height).fill({ color });
    }
  }
  onDestroy() {
    this.i18nRequestId += 1;
    this.i18nNeeded = false;
    this.resolvedSourceTexts.clear();
  }
  private resolveText(component: TextComponent): string {
    if (
      shouldHoldForMissingTranslation(component.text, this.translations, {
        sourceLocale: this.sourceLocale,
        targetLocale: this.targetLocale,
        i18nNeeded: this.i18nNeeded,
        firstI18nSettled: this.firstI18nSettled,
        resolvedSourceTexts: this.resolvedSourceTexts,
      })
    ) {
      return '';
    }
    return localizeText(
      component.text,
      component.replacements,
      this.sourceLocale,
      this.targetLocale,
      this.translations,
    );
  }
  private rememberSourceTexts(texts?: Iterable<string | undefined | null>) {
    addSourceTexts(this.knownSourceTexts, texts);
  }
  private collectSourceTexts(): string[] {
    for (const entry of Object.values(this.texts)) {
      this.rememberSourceTexts([entry.component.text]);
    }
    return [...this.knownSourceTexts];
  }
  private scheduleI18nRequest() {
    if (!this.sourceLocale || !this.firstI18nSettled || this.i18nRequestScheduled) return;
    this.i18nRequestScheduled = true;
    queueMicrotask(() => {
      this.i18nRequestScheduled = false;
      this.startI18nRequest();
    });
  }
  private startI18nRequest() {
    if (!this.sourceLocale || !this.firstI18nSettled) return;
    const sourceTexts = this.collectSourceTexts();
    if (
      !shouldRequestI18nForSourceTexts(sourceTexts, this.translations, {
        i18nNeeded: this.i18nNeeded,
        lastRequestedSourceTexts: this.lastRequestedSourceTexts,
      })
    ) {
      return;
    }
    void this.requestI18nAndApply();
  }
  private async requestI18nAndApply() {
    const sourceTexts = untranslatedSourceTexts(
      this.collectSourceTexts(),
      this.translations,
    );
    if (sourceTexts.length === 0) return;
    this.lastRequestedSourceTexts = sourceTextsSignature(sourceTexts);
    const requestId = ++this.i18nRequestId;
    const result = await requestHostI18n({
      sourceLocale: this.sourceLocale,
      sourceTexts,
    });
    if (requestId !== this.i18nRequestId) return;
    addSourceTexts(this.resolvedSourceTexts, sourceTexts);
    this.i18nNeeded = result.needed;
    if (!result.needed) {
      this.refreshAllText();
      return;
    }
    this.setI18n(result.locale, result.translations);
  }
  private refreshAllText() {
    for (const [id, entry] of Object.entries(this.texts)) {
      entry.text.text = this.resolveText(entry.component);
      const gameObject = entry.component.gameObject;
      const transform = gameObject?.getComponent(Transform2D);
      if (transform) {
        transform.size.width = entry.text.width;
        transform.size.height = entry.text.height;
      }
      this.syncBackground(Number(id));
    }
  }
}
